import { useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CardGiftcardRounded from '@mui/icons-material/CardGiftcardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { ApiError } from '@/api';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useAsync } from '@/hooks/useAsync';
import { formatDateTime, formatRelative } from '@/utils/format';
import { getCatalog, subscribe } from './api';
import {
  CYCLE_SHORT,
  formatDate,
  formatDueDate,
  formatMoney,
  isOpen,
  PAYMENT_STATUS,
  STATUS,
} from './labels';
import { DEFAULT_CHOICE, normalizeChoice } from './choice';
import { PlanPicker, QuoteSummary } from './PlanPicker';
import type {
  AccessStatus,
  FeatureKey,
  InstitutionBilling,
  InstitutionBillingDetail,
  Payment,
  PlanChoice,
} from './types';

const FEATURE_LABEL: Record<FeatureKey, string> = {
  assessments: 'Avaliações e correção automática',
  advanced_reports: 'Relatório individual',
  exports: 'Exportações',
  enade_content: 'Simulados ENADE',
  priority_support: 'Suporte prioritário',
  onboarding: 'Implantação assistida',
  enem: 'Simulados ENEM',
};

export function StatusChip({ status, blocked }: { status: AccessStatus; blocked?: boolean }) {
  const info = STATUS[blocked ? 'blocked' : status];
  return <Chip size="small" variant="soft" color={info.color} label={info.label} />;
}

/** The headline of an institution's access: what is going on and what to do about it. */
function StatusAlert({ billing }: { billing: InstitutionBilling }) {
  const sub = billing.subscription;
  const next = billing.nextPayment;
  const pay = next?.invoiceUrl && (
    <Button
      color="inherit"
      size="small"
      href={next.invoiceUrl}
      target="_blank"
      rel="noopener"
      endIcon={<OpenInNewRounded />}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Pagar {formatMoney(next.amountCents)}
    </Button>
  );
  if (billing.blocked) {
    return (
      <Alert severity="error">
        <AlertTitle>Acesso suspenso pela administração</AlertTitle>
        {billing.blockedReason}
      </Alert>
    );
  }
  switch (billing.status) {
    case 'courtesy':
      return (
        <Alert severity="success" icon={<CardGiftcardRounded />}>
          <AlertTitle>Acesso liberado por cortesia</AlertTitle>
          {sentence(sub?.note)}{' '}
          {sub?.endsAt ? `Vale até ${formatDate(sub.endsAt)}.` : 'Sem data para terminar.'}{' '}
          {sub &&
            (sub.grantedBy
              ? `Concedida por ${sub.grantedBy} em ${formatDate(sub.createdAt)}.`
              : `Registrada em ${formatDate(sub.createdAt)}.`)}
        </Alert>
      );
    case 'trial':
      return (
        <Alert severity="info" action={pay}>
          <AlertTitle>Período de teste até {formatDate(sub?.trialEndsAt)}</AlertTitle>
          {next
            ? `A primeira fatura (${formatMoney(next.amountCents)}) vence em ${formatDueDate(next.dueDate)}. Pode pagar antes, se preferir.`
            : 'Aproveite para cadastrar as turmas e liberar os primeiros simulados.'}
        </Alert>
      );
    case 'active':
      return (
        <Alert severity="success" action={next?.status === 'PENDING' ? pay : undefined}>
          <AlertTitle>Pagamentos em dia</AlertTitle>
          {sub?.paidUntil
            ? `Pago até ${formatDate(sub.paidUntil)}.`
            : 'O tempo do plano anterior continua valendo.'}
          {next &&
            ` Próxima fatura: ${formatMoney(next.amountCents)} em ${formatDueDate(next.dueDate)}.`}
        </Alert>
      );
    case 'overdue':
      return (
        <Alert severity="warning" action={pay}>
          <AlertTitle>Há uma fatura vencida</AlertTitle>
          Pague até {formatDate(billing.accessUntil)} para não perder o acesso.
        </Alert>
      );
    case 'pending':
      return (
        <Alert severity="warning" action={pay}>
          <AlertTitle>Aguardando o primeiro pagamento</AlertTitle>O acesso começa assim que o
          pagamento for confirmado (PIX: na hora; boleto: até 3 dias úteis).
        </Alert>
      );
    case 'expired':
      return (
        <Alert severity="error" action={pay}>
          <AlertTitle>O período pago terminou</AlertTitle>
          {next
            ? 'Pague a fatura em aberto para voltar a usar a plataforma.'
            : 'Contrate um plano para voltar a usar a plataforma.'}
        </Alert>
      );
    case 'ended':
      return (
        <Alert severity="error">
          <AlertTitle>A cortesia terminou</AlertTitle>
          {sentence(sub?.cancelReason)} Para continuar, contrate um plano.
        </Alert>
      );
    case 'canceled':
      return (
        <Alert severity={billing.hasAccess ? 'info' : 'error'}>
          <AlertTitle>Plano cancelado</AlertTitle>
          {billing.hasAccess
            ? `Sem novas cobranças. O acesso continua até ${formatDate(billing.accessUntil)}.`
            : 'Contrate um plano para voltar a usar a plataforma.'}
          {sub?.cancelReason && ` Motivo: ${sentence(sub.cancelReason)}`}
        </Alert>
      );
    default:
      return (
        <Alert severity="warning">
          <AlertTitle>Sem plano</AlertTitle>
          Contrate um plano para que estudantes e professores possam entrar.
        </Alert>
      );
  }
}

/** "Texto" → "Texto." (reasons are typed without a final period). */
const sentence = (text: string | null | undefined) =>
  text ? (/[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`) : '';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Box sx={{ fontWeight: 600, mt: 0.25 }}>{children}</Box>
    </Box>
  );
}

/** Plan, usage and status of one institution, with its invoices and history. */
export function BillingPanel({
  billing,
  actions,
  extra,
}: {
  billing: InstitutionBillingDetail;
  /** Buttons under the status (change plan, cancel; admin actions). */
  actions?: ReactNode;
  /** Shown before the invoices (e.g. the institution's data, for admins). */
  extra?: ReactNode;
}) {
  const sub = billing.subscription;
  const usage = billing.seatLimit ? Math.min(100, (billing.students / billing.seatLimit) * 100) : 0;
  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}
          >
            <Typography variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }}>
              {billing.name}
            </Typography>
            <StatusChip status={billing.status} blocked={billing.blocked} />
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2.5,
              mb: 2.5,
            }}
          >
            <Fact label="Plano">
              {sub ? (
                <>
                  {sub.planName}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}
                    · {sub.kind === 'courtesy' ? 'cortesia' : CYCLE_SHORT[sub.cycle]}
                  </Typography>
                </>
              ) : (
                '—'
              )}
            </Fact>
            <Fact label="Valor">
              {sub?.kind === 'paid' ? (
                <>
                  {formatMoney(sub.amountCents)}
                  {sub.cycle !== 'once' && sub.cycle !== 'monthly' && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}
                      ({formatMoney(sub.monthlyCents)}/mês)
                    </Typography>
                  )}
                </>
              ) : sub ? (
                'Sem cobrança'
              ) : (
                '—'
              )}
            </Fact>
            <Fact label="Estudantes">
              {billing.students.toLocaleString('pt-BR')}
              {billing.seatLimit ? (
                <>
                  {' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    de {billing.seatLimit.toLocaleString('pt-BR')}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={usage}
                    color={usage >= 100 ? 'error' : usage >= 90 ? 'warning' : 'primary'}
                    sx={{ mt: 0.75 }}
                    aria-label="Vagas de estudantes usadas"
                  />
                </>
              ) : (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' '}
                  · sem limite
                </Typography>
              )}
            </Fact>
            <Fact label="Acesso até">
              {billing.accessUntil
                ? formatDate(billing.accessUntil)
                : billing.hasAccess
                  ? 'Sem data para terminar'
                  : '—'}
            </Fact>
          </Box>
          <StatusAlert billing={billing} />
          {billing.seatLimit !== null && usage >= 90 && (
            <Alert severity={usage >= 100 ? 'error' : 'warning'} sx={{ mt: 1.5 }}>
              {usage >= 100
                ? 'O limite de estudantes do plano foi atingido: novos estudantes não conseguem se cadastrar.'
                : 'Quase no limite de estudantes do plano.'}{' '}
              Amplie o plano para continuar crescendo.
            </Alert>
          )}
          {billing.hasAccess && billing.features.length > 0 && (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.75, mt: 2 }}>
              {billing.features.map((f) => (
                <Chip key={f} size="small" variant="outlined" label={FEATURE_LABEL[f] ?? f} />
              ))}
              {billing.coordinatorLimit !== null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${billing.coordinators} de ${billing.coordinatorLimit} contas de coordenação`}
                />
              )}
            </Stack>
          )}
          {actions && (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
              {actions}
            </Stack>
          )}
        </CardContent>
      </Card>
      {extra}
      <PaymentsTable payments={billing.payments} />
      {billing.events.length > 0 && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Typography sx={{ fontWeight: 600 }}>Histórico ({billing.events.length})</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {billing.events.map((e) => (
              <Box
                key={e.id}
                sx={(t) => ({
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 160px 120px' },
                  gap: { xs: 0.25, sm: 2 },
                  px: 2.5,
                  py: 1.25,
                  borderTop: `1px solid ${t.vars.palette.divider}`,
                })}
              >
                <Typography variant="body2">{e.summary}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {e.actorName ?? 'Asaas / sistema'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  title={formatDateTime(e.createdAt)}
                  sx={{ textAlign: { sm: 'right' } }}
                >
                  {formatRelative(e.createdAt)}
                </Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
}

function PaymentsTable({ payments }: { payments: Payment[] }) {
  if (payments.length === 0) return null;
  return (
    <Card>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, px: 2.5, pt: 2 }}>
        Faturas
      </Typography>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell>Vencimento</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((p) => {
              const info = PAYMENT_STATUS[p.status] ?? {
                label: p.status,
                color: 'default' as const,
              };
              return (
                <TableRow key={p.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDueDate(p.dueDate)}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={p.description ?? undefined}>
                      {p.kind === 'setup' ? 'Implantação' : (p.description ?? 'Mensalidade')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(p.amountCents)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="soft" color={info.color} label={info.label} />
                    {p.paidAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        em {formatDate(p.paidAt)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {p.invoiceUrl && p.status !== 'DELETED' && (
                      <Button
                        size="small"
                        variant={isOpen(p.status) ? 'contained' : 'text'}
                        href={p.invoiceUrl}
                        target="_blank"
                        rel="noopener"
                      >
                        {isOpen(p.status) ? 'Pagar' : 'Ver'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

/** Contracts or changes the plan (the coordination; admins too). */
export function ChangePlanDialog({
  billing,
  open,
  onClose,
  onDone,
}: {
  billing: InstitutionBilling;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const notify = useNotify();
  const catalog = useAsync(getCatalog);
  const sub = billing.subscription;
  const [choice, setChoice] = useState<PlanChoice | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = catalog.data;
  const current: PlanChoice | null = data
    ? (choice ??
      normalizeChoice(data, {
        ...DEFAULT_CHOICE,
        ...(sub && sub.kind === 'paid'
          ? { plan: sub.planKey, cycle: sub.cycle as PlanChoice['cycle'], addons: sub.addons }
          : {}),
        students: Math.max(billing.students, sub?.seats ?? 0, 50),
      }))
    : null;

  const confirm = async () => {
    if (!current) return;
    setSaving(true);
    setError(null);
    try {
      const result = await subscribe(billing.id, current);
      notify('Plano contratado.', 'success');
      if (
        result.checkoutUrl &&
        result.institution.status !== 'active' &&
        result.institution.status !== 'trial'
      ) {
        window.open(result.checkoutUrl, '_blank', 'noopener');
      }
      setChoice(null);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível contratar o plano.');
    } finally {
      setSaving(false);
    }
  };

  const covered =
    billing.hasAccess && ['active', 'trial', 'courtesy', 'canceled'].includes(billing.status);
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {sub?.kind === 'paid' && !sub.canceledAt ? 'Mudar o plano' : 'Contratar um plano'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!data || !current ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
              gap: 3,
              alignItems: 'start',
              pt: 1,
            }}
          >
            <PlanPicker catalog={data} value={current} onChange={setChoice} dense />
            <QuoteSummary
              choice={current}
              footer={
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {covered && billing.accessUntil
                    ? 'O tempo já pago (ou o teste) continua valendo: a primeira fatura do novo plano vence quando ele terminar. Os recursos mudam na hora.'
                    : 'A fatura vence hoje. O acesso volta assim que o pagamento for confirmado.'}
                </Typography>
              }
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Voltar
        </Button>
        <Button variant="contained" onClick={() => void confirm()} disabled={saving || !current}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Confirmar plano'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Asks for a reason (and optionally more fields) before an action that goes on record. */
export function ReasonDialog({
  open,
  title,
  description,
  confirmLabel,
  danger,
  children,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  children?: ReactNode;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason('');
      setError(null);
    }
  }
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível concluir.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {description && (
            <Typography variant="body2" color="text.secondary" component="div">
              {description}
            </Typography>
          )}
          {children}
          <TextField
            label="Motivo (fica no histórico)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            autoFocus
            required
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color={danger ? 'error' : 'primary'}
          onClick={() => void submit()}
          disabled={saving || reason.trim().length < 3}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
